import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errMsg } from "../../lib/api";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Banner } from "../ui/Banner";
import { Badge } from "../ui/Badge";

type Step = "reset" | "shopify" | "odoo";

type Props = {
  open: boolean;
  initialStep: Step;
  onClose: () => void;
};

type CatalogStatus = {
  total_products: number;
  shopify_matched: number;
  shopify_missing: number;
  odoo_linked: number;
  odoo_missing: number;
  last_synced_at: string | null;
};

type JobStatus = "pending" | "running" | "done" | "error";
type CatalogJob = {
  id: number;
  type: string;
  status: JobStatus;
  products_imported: number;
  error: string | null;
  operator: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
};

type ResetResult = { products_imported: number; operator: string; at: string };
type ShopifyResult = {
  matched: number;
  no_match: number;
  updated: number;
  errors: string[];
  operator: string;
};
type OdooResult = {
  mode: string;
  archived: number;
  deleted: number;
  created: number;
  updated: number;
  operator: string;
};

const CONFIRM_TOKEN = "I-UNDERSTAND-DATA-WIPE";

const TITLES: Record<Step, string> = {
  reset: "⚠️ Cancellazione catalogo prodotti",
  shopify: "Riallineamento SKU Shopify",
  odoo: "Sincronizzazione catalogo Odoo",
};

const NEXT_LABEL: Record<Step, string> = {
  reset: "Esegui ora riallineamento Shopify →",
  shopify: "Esegui ora sync Odoo →",
  odoo: "Chiudi",
};

const NEXT_STEP: Record<Step, Step | null> = {
  reset: "shopify",
  shopify: "odoo",
  odoo: null,
};

export function CatalogAdminFlow({ open, initialStep, onClose }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>(initialStep);
  const [confirmInput, setConfirmInput] = useState("");
  const [jobId, setJobId] = useState<number | null>(null);
  const [resetResult, setResetResult] = useState<ResetResult | null>(null);
  const [shopifyResult, setShopifyResult] = useState<ShopifyResult | null>(null);
  const [odooResult, setOdooResult] = useState<OdooResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: status } = useQuery<CatalogStatus>({
    queryKey: ["catalog-status"],
    queryFn: async () => (await api.get("/api/catalog/status")).data,
    enabled: open,
    staleTime: 5_000,
  });

  useEffect(() => {
    if (!open) return;
    setStep(initialStep);
    setConfirmInput("");
    setError(null);
    setJobId(null);
    setResetResult(null);
    setShopifyResult(null);
    setOdooResult(null);
  }, [open, initialStep]);

  // Async full-reset: POST returns 202 + job row; poll until done/error.
  const resetRunning = jobId != null && !resetResult && !error;
  const { data: job } = useQuery<CatalogJob>({
    queryKey: ["catalog-job", jobId],
    queryFn: async () => (await api.get(`/api/catalog/jobs/${jobId}`)).data,
    enabled: resetRunning,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "pending" || s === "running" ? 1500 : false;
    },
  });

  useEffect(() => {
    if (!job) return;
    if (job.status === "done") {
      setResetResult({
        products_imported: job.products_imported,
        operator: job.operator ?? "",
        at: job.finished_at ?? "",
      });
      qc.invalidateQueries({ queryKey: ["catalog-status"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    } else if (job.status === "error") {
      setError(job.error ?? "Reset catalogo fallito");
      setJobId(null);
    }
  }, [job, qc]);

  const resetMut = useMutation({
    mutationFn: async () =>
      (await api.post<CatalogJob>("/api/catalog/full-reset", { confirm: CONFIRM_TOKEN })).data,
    onSuccess: (data) => {
      setJobId(data.id);
    },
    onError: (e) => setError(errMsg(e)),
  });

  const shopifyMut = useMutation({
    mutationFn: async () =>
      (await api.post<ShopifyResult>("/api/catalog/realign-shopify")).data,
    onSuccess: (data) => {
      setShopifyResult(data);
      qc.invalidateQueries({ queryKey: ["catalog-status"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    },
    onError: (e) => setError(errMsg(e)),
  });

  const odooMut = useMutation({
    mutationFn: async () =>
      (
        await api.post<OdooResult>("/api/catalog/sync-odoo", {
          mode: "wipe-and-recreate",
        })
      ).data,
    onSuccess: (data) => {
      setOdooResult(data);
      qc.invalidateQueries({ queryKey: ["catalog-status"] });
    },
    onError: (e) => setError(errMsg(e)),
  });

  const busy =
    resetMut.isPending || resetRunning || shopifyMut.isPending || odooMut.isPending;
  const result = step === "reset" ? resetResult : step === "shopify" ? shopifyResult : odooResult;

  const tone: "danger" | "warn" | "default" =
    step === "reset" ? "danger" : step === "shopify" ? "warn" : "default";

  const canRun = useMemo(() => {
    if (step === "reset") return confirmInput.trim() === CONFIRM_TOKEN;
    return true;
  }, [step, confirmInput]);

  function startNext() {
    setError(null);
    const next = NEXT_STEP[step];
    if (!next) {
      onClose();
      return;
    }
    setStep(next);
    setConfirmInput("");
  }

  function runStep() {
    setError(null);
    if (step === "reset") return resetMut.mutate();
    if (step === "shopify") return shopifyMut.mutate();
    if (step === "odoo") return odooMut.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={TITLES[step]}
      tone={tone}
      footer={
        result ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Chiudi
            </Button>
            <Button variant="yellow" style={{ flex: 1 }} onClick={startNext}>
              <Icon name="check" size={14} /> {NEXT_LABEL[step]}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Annulla
            </Button>
            <Button
              variant={step === "reset" ? "danger" : "primary"}
              style={{ flex: 1 }}
              disabled={!canRun || busy}
              onClick={runStep}
            >
              {busy
                ? "Esecuzione…"
                : step === "reset"
                  ? "Reset catalogo"
                  : step === "shopify"
                    ? "Avvia riallineamento"
                    : "Avvia sync Odoo"}
            </Button>
          </>
        )
      }
    >
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="danger" icon="x">
            {error}
          </Banner>
        </div>
      )}

      {step === "reset" && !resetResult && !resetRunning && (
        <ResetIntro status={status} confirmInput={confirmInput} setConfirmInput={setConfirmInput} />
      )}
      {step === "reset" && resetRunning && <ResetProgress job={job} />}
      {step === "shopify" && !shopifyResult && <ShopifyIntro status={status} />}
      {step === "odoo" && !odooResult && <OdooIntro status={status} />}

      {step === "reset" && resetResult && <ResetReport r={resetResult} />}
      {step === "shopify" && shopifyResult && <ShopifyReport r={shopifyResult} />}
      {step === "odoo" && odooResult && <OdooReport r={odooResult} />}
    </Modal>
  );
}

function ResetIntro({
  status,
  confirmInput,
  setConfirmInput,
}: {
  status?: CatalogStatus;
  confirmInput: string;
  setConfirmInput: (v: string) => void;
}) {
  const current = status?.total_products ?? 0;
  return (
    <>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--tcg-ink)" }}>
        Questa operazione cancellerà <strong>tutti i prodotti dal database locale</strong> e li
        reimporterà esclusivamente da TikTok Shop. Lo stock sarà azzerato e ripreso da TikTok
        inventory.
      </p>
      <ul
        style={{
          marginTop: 12,
          paddingLeft: 18,
          fontSize: 12,
          lineHeight: 1.6,
          color: "var(--tcg-ink-soft)",
        }}
      >
        <li>
          <strong>{current}</strong> prodotti correnti saranno rimpiazzati
        </li>
        <li>I prodotti manuali (SKU FGC-…) e i loro stock sono preservati</li>
        <li>Eventi storici (stock_events) mantenuti per audit</li>
        <li>
          La sincronizzazione verso Shopify/Odoo avviene solo quando le relative integrazioni sono
          configurate e attive (al momento non lo sono)
        </li>
      </ul>
      <div style={{ marginTop: 16 }}>
        <label
          style={{
            display: "block",
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--tcg-muted)",
            marginBottom: 6,
          }}
        >
          Digita <span className="tcg-mono">{CONFIRM_TOKEN}</span> per confermare
        </label>
        <input
          className="tcg-input tcg-mono"
          autoFocus
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.target.value.toUpperCase())}
          placeholder={CONFIRM_TOKEN}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="characters"
        />
      </div>
    </>
  );
}

function ResetProgress({ job }: { job?: CatalogJob }) {
  const running = job?.status === "running";
  return (
    <div style={{ textAlign: "center", padding: "12px 0" }}>
      <Banner tone="warn" icon="clock">
        {running
          ? "Import da TikTok Shop in corso… (200+ prodotti, alcune pagine)"
          : "Job in coda, avvio imminente…"}
      </Banner>
      <p style={{ marginTop: 10, fontSize: 11, color: "var(--tcg-ink-soft)" }}>
        Puoi attendere qui: l'operazione gira in background sul server e la finestra si aggiorna da
        sola al termine.
      </p>
    </div>
  );
}

function ShopifyIntro({ status }: { status?: CatalogStatus }) {
  const total = status?.total_products ?? 0;
  const missing = status?.shopify_missing ?? 0;
  return (
    <>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--tcg-ink)" }}>
        Per ogni prodotto locale verrà cercato il variant Shopify corrispondente per titolo (fuzzy
        match ≥ 0.85) e il suo SKU verrà <strong>riscritto al TikTok product_id</strong>.
      </p>
      <ul
        style={{
          marginTop: 12,
          paddingLeft: 18,
          fontSize: 12,
          lineHeight: 1.6,
          color: "var(--tcg-ink-soft)",
        }}
      >
        <li>
          <strong>{total}</strong> prodotti locali in scansione
        </li>
        <li>
          <strong>{missing}</strong> attualmente senza match Shopify
        </li>
        <li>SKU senza match restano con sync_to_shopify=false (revisione manuale)</li>
        <li>L'operazione è atomica per ogni prodotto, errori sono raccolti nel report</li>
      </ul>
    </>
  );
}

function OdooIntro({ status }: { status?: CatalogStatus }) {
  const total = status?.total_products ?? 0;
  return (
    <>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--tcg-ink)" }}>
        <strong>Wipe-and-recreate</strong>: i prodotti Odoo con ordini di vendita saranno
        archiviati (active=false), gli altri eliminati. Poi i <strong>{total}</strong> prodotti
        locali saranno ricreati con <span className="tcg-mono">default_code = TikTok product_id</span>.
      </p>
      <ul
        style={{
          marginTop: 12,
          paddingLeft: 18,
          fontSize: 12,
          lineHeight: 1.6,
          color: "var(--tcg-ink-soft)",
        }}
      >
        <li>Operazione può durare 2-3 minuti per 200+ prodotti Odoo</li>
        <li>Prezzo, costo e tipo (stockable) sincronizzati da TCG-Inv-Hub</li>
        <li>Stock fisico Odoo NON viene resettato (gestito separatamente)</li>
      </ul>
    </>
  );
}

function ResetReport({ r }: { r: ResetResult }) {
  return (
    <>
      <Banner tone="success" icon="check">
        Catalogo rigenerato da TikTok.
      </Banner>
      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Stat label="Prodotti importati" value={r.products_imported} tone="success" />
        <Stat label="Operatore" value={r.operator} mono />
      </div>
    </>
  );
}

function ShopifyReport({ r }: { r: ShopifyResult }) {
  return (
    <>
      <Banner tone={r.errors.length > 0 ? "warn" : "success"} icon="check">
        Riallineamento Shopify completo.
      </Banner>
      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Stat label="Matched" value={r.matched} tone="success" />
        <Stat label="Senza match" value={r.no_match} tone="warn" />
        <Stat label="SKU aggiornati" value={r.updated} tone="success" />
        <Stat label="Errori" value={r.errors.length} tone={r.errors.length > 0 ? "danger" : "default"} />
      </div>
      {r.errors.length > 0 && (
        <details style={{ marginTop: 12, fontSize: 11, color: "var(--tcg-ink-soft)" }}>
          <summary style={{ cursor: "pointer", fontWeight: 700 }}>Vedi errori ({r.errors.length})</summary>
          <ul style={{ marginTop: 6, paddingLeft: 16 }}>
            {r.errors.slice(0, 20).map((e, i) => (
              <li key={i} className="tcg-mono" style={{ fontSize: 10, marginTop: 2 }}>
                {e}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

function OdooReport({ r }: { r: OdooResult }) {
  return (
    <>
      <Banner tone="success" icon="check">
        Sync Odoo {r.mode} completo.
      </Banner>
      <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Stat label="Archiviati" value={r.archived} tone="warn" />
        <Stat label="Eliminati" value={r.deleted} tone="default" />
        <Stat label="Creati" value={r.created} tone="success" />
        <Stat label="Aggiornati" value={r.updated} tone="success" />
      </div>
      <div style={{ marginTop: 12 }}>
        <Badge tone="navy">Modalità: {r.mode}</Badge>
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  tone = "default",
  mono,
}: {
  label: string;
  value: number | string;
  tone?: "default" | "success" | "warn" | "danger";
  mono?: boolean;
}) {
  const fg =
    tone === "success"
      ? "var(--tcg-success)"
      : tone === "warn"
        ? "var(--tcg-warn)"
        : tone === "danger"
          ? "var(--tcg-danger)"
          : "var(--tcg-ink)";
  const bg =
    tone === "success"
      ? "var(--tcg-success-bg)"
      : tone === "warn"
        ? "var(--tcg-warn-bg)"
        : tone === "danger"
          ? "var(--tcg-danger-bg)"
          : "var(--tcg-bg)";
  return (
    <div style={{ background: bg, padding: "8px 10px", borderRadius: 10 }}>
      <div
        className={mono ? "tcg-mono" : "tcg-num"}
        style={{ fontSize: 18, fontWeight: 900, color: fg }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: fg,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  );
}
